import { Box, useToken } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaPen } from 'react-icons/fa';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import { useGetConfirmedGrafts } from '@/hooks/service-hooks/confirmedGrafts';
import { useConfirmedGraftsTutorial } from '@/hooks/tutorialsHooks/useGraftLogsTutorial';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getConfirmedGrafts } from '@/services/confirmedGrafts';
import { useAuthStore } from '@/store';
import { useTourContext } from '@/store/tourContext';
import type { IConfirmedTissue } from '@/types/confirmedGrafts';
import type { IUnassignedTissue } from '@/types/graftsToAssigned';
import type { FilterType } from '@/types/table';
import { formatDate } from '@/utils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { generateMockConfirmedTissueData } from '@/utils/tutorials/mockData';
import { EditForm } from '../EditForm';
import { columns } from './column';

const CLINIC_KEY = '_ta_clinic_value';

const ConfirmedGraftsTable = () => {
	// Modal state management
	const { hasPermission } = usePermission();
	const { defaultClinicNames,defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY, true);
	const [brand600] = useToken('colors', ['brand.600']);
	const [isFormDirty, setIsFormDirty] = useState(false);
	const [modalInformation, setModalInformation] = useState<{
		mode: 'create' | 'edit' | 'details' | null;
		data: string | Partial<IUnassignedTissue> | null;
		element: React.ReactNode | null;
	}>({
		mode: null,
		data: null,
		element: null,
	});
	const confirmedGraftsTableRef = useRef<HTMLDivElement | null>(null);
	const user = useAuthStore((state) => state.user);

	const allColumns = useMemo(() => {
		if (hasPermission(['ConfirmedGrafts:Edit'])) {
			return columns;
		}
		return columns.filter((item) => item.key !== 'action');
	}, [hasPermission]);

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicNames.length > 0,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicNames.length
				? [
					{
						key: CLINIC_KEY,
						values: defaultClinicNames,
					},
					{
						key: '_ta_clinic_ids',
						values: defaultClinicIds,
					}
				]
				: [],
		),
		sort: '',
		port: user?.userInformation?.portsAvailable?.trim() || '',
	});

	const { data, isFetching, refetch } = useGetConfirmedGrafts(filters);
	const { activeTutorialName } = useTourContext();
	const isGraftLogsTutorialActive = activeTutorialName === 'OnboardingTutorialGraftLogs';

	// Register tutorial refs
	useConfirmedGraftsTutorial({
		confirmedGraftsTableRef,
	});

	// Define handleEdit before useMemo to avoid initialization error
	const handleEdit = useCallback((item: any) => {
		setIsFormDirty(false);
		setModalInformation({
			mode: 'edit',
			data: item,
			element: (
				<EditForm
					type="confirm"
					initialData={item}
					onSuccess={() => {
						setModalInformation({ mode: null, data: null, element: null });
						setIsFormDirty(false);
						refetch();
					}}
					isLoading={false}
					onIsDirtyChange={setIsFormDirty}
				/>
			),
		});
	}, [refetch]);

	// Handle filter apply
	const onFilterApply = useCallback(({
			page,
			limit,
			status,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters, with clinic names in this case
			const mappedFilters = mapGlobalFilters(globalFilters || [], true);
			const modifiedGlobalFilters = [
				...mappedFilters,
				{
					key: '_ta_clinic_ids',
					values: defaultClinicIds
				}
			];

			// Update pagination state to trigger API call
			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				search,
				enabled: defaultClinicNames.length > 0,
				status: status?.value,
				globalFilters: JSON.stringify(modifiedGlobalFilters),
				sort: sort ? `${sort.key}|${sort.direction}` : '',
				port: user?.userInformation?.portsAvailable?.trim() || '',
			});
		}, [mapGlobalFilters]);

	const transformedData = useMemo(() => {
		// IMPORTANT: Only use mock data when the GraftLogs tutorial is ACTIVE
		// When tutorial is NOT active, use real API data
		if (isGraftLogsTutorialActive) {
			// Tutorial is active - use mock data
			const mockData = generateMockConfirmedTissueData(10);
			return mockData.map((item) => {
				return {
					id: '',
					Physician: (
						<FaceliftTruncatedTextWithTooltip
							text={item.Physician || '-'}
							maxLength={20}
						/>
					),
					Patient: (
						<FaceliftTruncatedTextWithTooltip
							text={item.Patient || '-'}
							maxLength={20}
						/>
					),
					DateofService: formatDate(item.DateofService) || '-',
					TissueID: item.TissueID || '-',
					Product: item.Product || '-',
					Size: item.Size || '-',
					action: (
						<FaceliftButton
							size={'2xs'}
							border={'none'}
							bg={'transparent'}
							color={'{colors.text}'}
							variant={'outline'}
							_hover={{ color: '{colors.brand.600}' }}
							onClick={() => handleEdit(item)}
						>
							<FaPen />
						</FaceliftButton>
					),
				};
			});
		}

		if (!data?.items) return [];

		return data?.items.map((item) => {
			return {
				id: '',
				Physician: (
					<FaceliftTruncatedTextWithTooltip
						text={item.Physician || '-'}
						maxLength={20}
					/>
				),
				Patient: (
					<FaceliftTruncatedTextWithTooltip
						text={item.Patient || '-'}
						maxLength={20}
					/>
				),
				DateofService: formatDate(item.DateofService) || '-',
				TissueID: item.TissueID || '-',
				Product: item.Product || '-',
				Size: item.Size || '-',
				action: (
					<FaceliftButton
						size={'2xs'}
						border={'none'}
						bg={'transparent'}
						color={'{colors.text}'}
						variant={'outline'}
						_hover={{ color: '{colors.brand.600}' }}
						onClick={() => handleEdit(item)}
					>
						<FaPen />
					</FaceliftButton>
				),
			};
		});
	}, [data, isGraftLogsTutorialActive, handleEdit]);

	const transformedDataToExport = (data: IConfirmedTissue[]) => {
		if (!data) return [];

		return data.map((item) => {
			return {
				id: '',
				Physician: item.Physician || '-',
				Patient: item.Patient || '-',
				DateofService: formatDate(item.DateofService) || '-',
				TissueID: item.TissueID || '-',
				Product: item.Product || '-',
				Size: item.Size || '-',
			};
		});
	};

	// Export functions
	const exportAllAsCSV = async () => {
		try {

			// Fetch all data with current filters
			const data = await getConfirmedGrafts({
				page: 1,
				limit: 9999,
				columnsFilters: '[]',
				search: filters.search || '',
				globalFilters: filters.globalFilters,
				sort: filters.sort ? filters.sort : '',
			});

			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(
				columns.filter((item) => item.key !== 'action'),
				transformedData,
				`Confirmed-Grafts-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Csv:', error);
			toast.error('Failed to export Csv');
		}
	};

	const exportAllAsExcel = async () => {
		try {

			// Fetch all data with current filters
			const data = await getConfirmedGrafts({
				page: 1,
				limit: 9999,
				columnsFilters: '[]',
				search: filters.search || '',
				globalFilters: filters.globalFilters,
				sort: filters.sort ? filters.sort : '',
			});

			const transformedData = transformedDataToExport(data?.items || []);
			generateExcelFromTable(
				columns.filter((item) => item.key !== 'action'),
				transformedData,
				`Confirmed-Grafts-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null, element: null });
		setIsFormDirty(false);
	};

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<DataTable
					tableName="Confirmed Grafts"
					ref={confirmedGraftsTableRef}
					loading={isFetching}
					items={transformedData}
					columns={allColumns}
					topLabel="Confirmed Grafts"
					totalCount={data?.meta.totalRecords || 0}
					onFilterApply={onFilterApply}
					searchable
					bordered
					hideShowColumn
					expandable
					paginated
					fullscreen
					exportable
					globalFilter={{
						title: 'Filters',
						items: [
							{
								options: defaultClinicNames.map((clinic) => ({
									label: clinic,
									id: clinic,
								})),
								key: 'Clinics',
								name: 'Clinics',
							},
						],
					}}
					customExportItemsData={transformedDataToExport(data?.items || [])}
					csv
					excel
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
				/>
			</Box>
			<FaceliftDialogWithExitConfirmation
				showBackBtn={true}
				open={
					modalInformation.mode === 'create' || modalInformation.mode === 'edit'
				}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				title={
					modalInformation.mode === 'create'
						? 'Add New Graft Log'
						: 'Edit Graft Log'
				}
				size="xl"
				sx={{
					width: 700,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflow: 'visible',
				}}
				isDirty={isFormDirty}
			>
				{modalInformation.element}
			</FaceliftDialogWithExitConfirmation>
		</>
	);
};

export default ConfirmedGraftsTable;
